import type * as zod from 'zod';

import * as log from '@sb/log';
import { FailureKind } from '@sb/routine-runner/FailureKind';
import type { StepFailure } from '@sb/routine-runner/types';
import { formatImuCollisionMessage } from '@sb/routine-runner/types';
import { checkEnabledCBFViolations } from '@sb/routine-runner/util/complianceCBF';
import { createEventDrivenWaiter } from '@sb/routine-runner/util/eventDrivenWaiter';
import {
  TeleopSessionOverridesRequestModeEnum,
  TeleopStateStatusEnum,
  type TeleopSessionOverridesRequest,
  type TeleopState,
} from '@sb/sb-robot-sdk/src/codegen/typescript-axios';
import { getFeatureFlagSync } from '@sb/service-interfaces/host/featureFlags';
import { loadHostIdentity } from '@sb/service-interfaces/host/identity';
import { EventEmitter, wait } from '@sb/utilities';

import type { StepAction, StepPlayArguments } from '../Step';
import Step from '../Step';

import Arguments, { type TeleopMode } from './Arguments';
import Variables, { type TeleopExitReason } from './Variables';

// Primary-bot teleop completion/errors are detected via a pushed StateChanged
// event from teleop-bot. This interval re-checks aggregated state on a timer:
// it is the backstop for a dropped primary-bot event, and it is also the sole
// detection path for secondary-bot errors, which are aggregated in the API
// layer at getState() time and never produce a push. Either way it is not a
// hot poll — 1 s vs. the old 250 ms.
const TELEOP_RECONCILE_INTERVAL_MS = 1000;
const TELEOP_MODE_SWAP_TIMEOUT_MS = 5000;
const TELEOP_MODE_SWAP_POLL_INTERVAL_MS = 250;

function stepModeToApiMode(
  mode: TeleopMode,
): TeleopSessionOverridesRequest['mode'] {
  switch (mode) {
    case 'arHeadset':
      return TeleopSessionOverridesRequestModeEnum.Headset;
    case 'iosPhone':
    case 'iosTablet':
      return TeleopSessionOverridesRequestModeEnum.Ios;
    case 'hapticDevice':
      return TeleopSessionOverridesRequestModeEnum.HapticDevice;
    default: {
      const _exhaustive: never = mode;

      return _exhaustive;
    }
  }
}

function deviceLabelForMode(mode: TeleopMode): string {
  switch (mode) {
    case 'arHeadset':
      return 'AR headset';
    case 'iosPhone':
      return 'iOS phone';
    case 'iosTablet':
      return 'iOS tablet';
    case 'hapticDevice':
      return 'Haptic device';
    default: {
      const _exhaustive: never = mode;

      return _exhaustive;
    }
  }
}

const ns = log.namespace('TeleopStep');

type Arguments = zod.infer<typeof Arguments>;
type Variables = zod.infer<typeof Variables>;

export default class TeleopStep extends Step<Arguments, Variables> {
  private events = new EventEmitter<{
    stop: void;
    complete: { exitReason: TeleopExitReason };
    failure: StepFailure;
  }>();

  public static areSubstepsRequired = false;

  public static Arguments = Arguments;

  public static Variables = Variables;

  /**
   * Timeout handle for automatic step termination
   * @internal
   */
  private timeoutHandle: NodeJS.Timeout | null = null;

  /**
   * Cleanup function for teleop state monitoring
   * @internal
   */
  private stopTeleopMonitoring: (() => void) | null = null;

  /**
   * Tracks whether teleop session was ever active during this step
   * @internal
   */
  private teleopWasActive = false;

  private pauseReason: string | undefined;

  public initializeVariableState(): void {
    this.variables = {
      status: 'waiting',
      exitReason: undefined,
    };
  }

  public async _play({ fail }: StepPlayArguments): Promise<void> {
    this.teleopWasActive = false;
    // Reset state for every Loop iteration; stale exitReason or
    // pauseReason from a prior pass leaks into this run's failure path.
    this.setVariable('status', 'waiting');
    this.setVariable('exitReason', undefined);
    this.pauseReason = undefined;

    if (this.routineContext.robot.robotKind !== 'live') {
      return fail({
        failure: {
          kind: FailureKind.StepPlayFailure,
          stepKind: 'Teleop',
        },
        failureReason: 'Teleop step not supported in visualizer',
      });
    }

    // Require CSM feature flag
    if (!getFeatureFlagSync('useControlSourceManager', false)) {
      return fail({
        failure: {
          kind: FailureKind.StepPlayFailure,
          stepKind: 'Teleop',
        },
        failureReason:
          'Teleop step requires the Control Source Manager feature flag to be enabled',
      });
    }

    // Teleoperation only functions on a robot in echo (AI) mode. The editor
    // validator normally blocks play before we get here; this defensive check
    // covers API/agent-started routines that skip frontend validation.
    const identity = await loadHostIdentity();

    if (identity?.robotOperationMode !== 'echo') {
      return fail({
        failure: {
          kind: FailureKind.StepPlayFailure,
          stepKind: 'Teleop',
        },
        failureReason:
          'Teleop step requires the robot to be in Echo (AI) mode.',
      });
    }

    // Refuse to start teleop if the robot currently violates any enabled CBF
    // (e.g. the tooltip is below the EE-Z bound). The shared check owns the
    // complianceMode flag + missing-connector policy; on violation we fail the
    // routine with the specific reason rather than silently waiting while every
    // operator /teleop/start attempt is rejected by the same check.
    const cbf = await checkEnabledCBFViolations(
      this.routineContext.complianceControlBotConnector,
    );

    if (!cbf.ok) {
      return fail({
        failure: {
          kind: FailureKind.StepPlayFailure,
          stepKind: 'Teleop',
        },
        failureReason: `Cannot start teleop: ${cbf.reason}`,
      });
    }

    // Haptic (Touch X) teleop can't run without a physically connected haptic
    // device. Fail fast here rather than waiting out the client-connect
    // deadline below, so the operator gets an immediate, specific reason.
    if (this.args.mode === 'hapticDevice') {
      const hasConnectedHapticDevice = this.routineContext.equipment
        .getDevicesByType('HapticDevice')
        .some((device) => {
          const state = device.update();

          return state.kind === 'HapticDevice' && state.isConnected;
        });

      if (!hasConnectedHapticDevice) {
        return fail({
          failure: {
            kind: FailureKind.StepPlayFailure,
            stepKind: 'Teleop',
          },
          failureReason:
            'No haptic device connected. Connect and enable a haptic device in Equipment settings.',
        });
      }
    }

    const { sdk } = this.routineContext;

    // Reject hand-edited routine JSON where both gripper bounds are set
    // but inverted. Single-sided overrides fall back to persisted, so we
    // can't validate them against an unknown other side here.
    if (
      this.args.gripperMin != null &&
      this.args.gripperMax != null &&
      this.args.gripperMin >= this.args.gripperMax
    ) {
      return fail({
        failure: {
          kind: FailureKind.StepPlayFailure,
          stepKind: 'Teleop',
        },
        failureReason: `Invalid gripper bounds: gripperMin (${this.args.gripperMin}) must be strictly less than gripperMax (${this.args.gripperMax}).`,
      });
    }

    // Pre-stage step overrides before the operator's press → start RPC
    // so the modality reapply doesn't run inside the start path.
    let modeWasApplied = false;

    if (sdk?.teleop?.setSessionOverrides) {
      const patch: TeleopSessionOverridesRequest = {
        mode: stepModeToApiMode(this.args.mode),
      };

      if (this.args.gripperVelocity != null) {
        patch.gripperVelocity = this.args.gripperVelocity;
      }

      if (this.args.gripperMin != null) {
        patch.gripperMin = this.args.gripperMin;
      }

      if (this.args.gripperMax != null) {
        patch.gripperMax = this.args.gripperMax;
      }

      try {
        const result = await sdk.teleop.setSessionOverrides(patch);

        if (result?.data?.status === TeleopStateStatusEnum.Error) {
          log.warn(ns`play`, 'Failed to apply step teleop settings', {
            patch,
            errors: result.data.errors,
          });
        } else {
          modeWasApplied = true;
        }
      } catch (err) {
        log.warn(ns`play`, 'Failed to apply step teleop settings', {
          patch,
          err,
        });
      }
    }

    if (sdk?.teleop?.getState) {
      const deadline =
        Date.now() + (modeWasApplied ? TELEOP_MODE_SWAP_TIMEOUT_MS : 0);

      let lastClientCount = 0;
      let lastClientTypes: Array<string | undefined> = [];

      do {
        // _stop() during this loop sets exitReason; the Promise.race listener
        // below isn't attached yet, so a fired failure event would be lost.
        if (this.variables.exitReason !== undefined) break;

        try {
          const teleopState = await sdk.teleop.getState();
          const connectedClients = teleopState.data.connectedClients ?? [];

          lastClientCount = connectedClients.length;

          lastClientTypes = connectedClients.map(
            (c: { clientType?: string }) => c.clientType,
          );

          if (lastClientCount > 0) {
            break;
          }
        } catch (err) {
          log.warn(ns`play`, 'Failed to check teleop client status', { err });
        }

        if (Date.now() >= deadline) {
          break;
        }

        await wait(TELEOP_MODE_SWAP_POLL_INTERVAL_MS);
      } while (Date.now() < deadline);

      if (this.variables.exitReason !== undefined) {
        return fail({
          failure: {
            kind: FailureKind.StepPlayFailure,
            stepKind: 'Teleop',
          },
          failureReason:
            this.pauseReason ||
            this.getFailureMessage('Teleop step was interrupted'),
        });
      }

      if (lastClientCount === 0) {
        const deviceLabel = deviceLabelForMode(this.args.mode);

        return fail({
          failure: {
            kind: FailureKind.StepPlayFailure,
            stepKind: 'Teleop',
          },
          failureReason: `No teleop client connected. Make sure your ${deviceLabel} is connected.`,
        });
      }

      log.info(ns`play`, 'Teleop client connected', {
        clientCount: lastClientCount,
        clients: lastClientTypes,
        mode: this.args.mode,
      });
    }

    try {
      log.info(ns`play`, 'Teleop step started, waiting for operator', {
        timeoutSeconds: this.args.timeoutSeconds,
      });

      this.setVariable('status', 'waiting');

      // Set up timeout
      this.setupTimeout();

      // Start monitoring teleop state
      this.startTeleopMonitoring();

      // Wait for completion or failure
      const outcome = await Promise.race([
        this.events.next('complete'),
        this.events.next('failure').then((failure) => Promise.reject(failure)),
      ]);

      // Set final state
      this.setVariable('status', 'completed');
      this.setVariable('exitReason', outcome.exitReason);

      log.info(ns`play`, 'Teleop step completed', {
        exitReason: outcome.exitReason,
      });
    } catch (error) {
      // Handle failure case - this stops the routine
      if ((error as StepFailure).failure) {
        // This is a StepFailure from events (Timeout, Interrupted, etc.)
        this.setVariable('status', 'failed');

        return fail(error as StepFailure);
      }

      // Unexpected error - fail the routine
      this.setVariable('status', 'failed');
      this.setVariable('exitReason', 'Error');

      return fail({
        failure: {
          kind: FailureKind.StepPlayFailure,
          stepKind: 'Teleop',
        },
        failureReason: `Teleop step failed: ${(error as Error)?.message}`,
        error: error as Error,
      });
    } finally {
      // Stop teleop monitoring
      if (this.stopTeleopMonitoring) {
        this.stopTeleopMonitoring();
        this.stopTeleopMonitoring = null;
      }

      // Clear timeout
      if (this.timeoutHandle) {
        clearTimeout(this.timeoutHandle);
        this.timeoutHandle = null;
      }
    }
  }

  public async _pause(args: StepAction): Promise<void> {
    log.warn(
      ns`pause`,
      'Pause on "teleop" step is not supported, stopping step.',
    );

    this.pauseReason = args.reason;
    await this._stop();
  }

  public async _stop(): Promise<void> {
    // Stop teleop monitoring
    if (this.stopTeleopMonitoring) {
      this.stopTeleopMonitoring();
      this.stopTeleopMonitoring = null;
    }

    // Clear timeout
    if (this.timeoutHandle) {
      clearTimeout(this.timeoutHandle);
      this.timeoutHandle = null;
    }

    // Cancel teleop on teleop-bot before the rest of routine teardown runs.
    // Skip on a clean operator-initiated exit ('Completed') — the session
    // is already over and the RPC would just be a logged no-op. Bound the
    // await so an unresponsive teleop-bot can't hang routine teardown.
    if (this.variables.exitReason !== 'Completed') {
      // Attach .catch before racing so a late rejection (after wait()
      // wins) is absorbed instead of leaking as an unhandled rejection.
      const stopCall = this.routineContext.sdk?.teleop
        ?.stopTeleop?.(undefined, undefined, true)
        ?.catch((err: unknown) => {
          log.warn(ns`stop`, 'Failed to force-stop teleop on teleop-bot', {
            err,
          });
        });

      await Promise.race([stopCall, wait(3000)]);
    }

    // Set exit reason if not already set (e.g., from timeout or completion)
    if (!this.variables.exitReason) {
      this.setVariable('exitReason', 'Interrupted');
      this.setVariable('status', 'failed');

      const failureMessage =
        this.pauseReason || // Show pause reason like collision or IK if available
        this.getFailureMessage(
          'Teleop step was interrupted with a pause', // fallback default message
        );

      // Emit failure to unblock Promise.race in _play()
      this.events.emit('failure', {
        failure: {
          kind: FailureKind.StepPlayFailure,
          stepKind: 'Teleop',
        },
        failureReason: failureMessage,
      });
    }

    log.info(ns`stop`, 'Teleop step stopped');
  }

  public hasSelfTimeout(): boolean {
    // We handle our own timeout
    return false;
  }

  /**
   * Set up the step timeout, fail the step if the teleop session did not complete within the timeout.
   */
  private setupTimeout(): void {
    this.timeoutHandle = setTimeout(() => {
      log.warn(ns`timeout`, 'Teleop step timeout reached', {
        timeoutSeconds: this.args.timeoutSeconds,
        teleopWasActive: this.teleopWasActive,
      });

      this.setVariable('exitReason', 'Timeout');

      // The timeout bounds the whole step, so it can also fire mid-session —
      // don't claim no operator connected when one did.
      const failureReason = this.teleopWasActive
        ? `Teleop session did not complete within its ${this.args.timeoutSeconds}s timeout.`
        : `No teleop operator took control within ${this.args.timeoutSeconds}s.`;

      this.events.emit('failure', {
        failure: {
          kind: FailureKind.StepPlayFailure,
          stepKind: 'Teleop',
        },
        failureReason,
      });
    }, this.args.timeoutSeconds * 1000);
  }

  /**
   * Start monitoring teleop state to detect when operator calls /teleop/start and /teleop/stop.
   *
   * The teleop client (WebXR/AR headset) controls the teleop session via public API:
   * 1. Operator calls /teleop/start → status transitions to TELEOP
   * 2. Operator calls /teleop/stop → status transitions to STOPPED
   *
   * Exit reasons:
   * - 'Completed': Teleop started and stopped successfully → routine continues
   * - 'Timeout': Step timeout reached before teleop completed → routine fails
   * - 'Error': Teleop error occurred (primary or secondary bot) → routine fails
   * - 'Interrupted': Step was stopped externally → routine fails
   */
  private startTeleopMonitoring(): void {
    const { sdk } = this.routineContext;

    if (!sdk) {
      const errorMessage =
        'Public API SDK is not available, failing teleop step';

      log.error(ns`monitoring`, errorMessage);

      this.setVariable('exitReason', 'Error');

      this.events.emit('failure', {
        failure: {
          kind: FailureKind.StepPlayFailure,
          stepKind: 'Teleop',
        },
        failureReason: errorMessage,
      });

      return;
    }

    const connector = this.routineContext.teleopBotConnector;

    // React to teleop-bot's pushed status transitions instead of polling; the
    // reconcile interval is the backstop if an event is ever dropped.
    const waiter = createEventDrivenWaiter({
      subscribe: (wake) => {
        if (!connector) {
          return () => {};
        }

        connector.subscribeToStateChanged(() => wake());

        return () => connector.unsubscribeFromStateChanged();
      },
      reconcileIntervalMs: TELEOP_RECONCILE_INTERVAL_MS,
    });

    this.stopTeleopMonitoring = waiter.stop;

    // Reads the aggregated teleop state (including secondary bots) and decides
    // whether the step is done. Runs on each pushed event and each reconcile.
    const evaluate = async (): Promise<{
      triggered: boolean;
      context?: TeleopState;
    }> => {
      // Use public api to get aggregated state including secondary bots
      const response = await sdk.teleop.getState();
      const state = response.data;

      // Check for errors on secondary bots
      const secondaryErrors = this.getSecondaryBotErrors(state);

      if (secondaryErrors.length > 0) {
        log.error(ns`monitoring`, 'Secondary bot error detected', {
          errors: secondaryErrors,
        });

        return {
          triggered: true,
          context: {
            ...state,
            status: TeleopStateStatusEnum.Error,
            errors: secondaryErrors,
          },
        };
      }

      // Track teleop state transitions based on operator's API calls
      switch (state.status) {
        case TeleopStateStatusEnum.Teleop:
          // Operator called /teleop/start - teleop session is active
          if (!this.teleopWasActive) {
            this.teleopWasActive = true;
            this.setVariable('status', 'active');

            log.info(ns`monitoring`, 'Operator started teleop session');

            // Add 'teleop' tag to recording when teleop starts
            await this.conditionallyAddRecordingTag('teleop');
          }

          return { triggered: false };

        case TeleopStateStatusEnum.Stopped:
          // Operator called /teleop/stop - check if this was a completed exit
          if (this.teleopWasActive) {
            log.info(ns`monitoring`, 'Operator stopped teleop session');

            return { triggered: true, context: state };
          }

          // Still waiting for operator to start teleop
          return { triggered: false };

        case TeleopStateStatusEnum.WaitForTeleop:
          // Still waiting for operator/device to start teleop
          return { triggered: false };

        case TeleopStateStatusEnum.Error:
          // Teleop error occurred - this will fail the routine
          log.error(ns`monitoring`, 'Teleop error detected', {
            errors: state.errors,
          });

          // Add 'teleop-error' tag to recording when teleop error occurs
          await this.conditionallyAddRecordingTag('teleop-error');

          return { triggered: true, context: state };

        case TeleopStateStatusEnum.Stopping:
          // Teleop is stopping, wait for it to finish
          return { triggered: false };

        default:
          return { triggered: false };
      }
    };

    const handleTriggered = (context?: TeleopState): void => {
      if (!context) {
        return;
      }

      if (context.status === TeleopStateStatusEnum.Error) {
        // Teleop error - fail the routine
        this.setVariable('exitReason', 'Error');

        const fallbackMessage = `Teleop error: ${context.errors?.join(', ') || 'Unknown error'}`;

        this.events.emit('failure', {
          failure: {
            kind: FailureKind.StepPlayFailure,
            stepKind: 'Teleop',
          },
          failureReason: this.getFailureMessage(fallbackMessage),
        });
      } else if (context.status === TeleopStateStatusEnum.Stopped) {
        // Completed exit - routine continues
        this.events.emit('complete', { exitReason: 'Completed' });
      }
    };

    (async () => {
      while (!waiter.isStopped()) {
        try {
          const { triggered, context } = await evaluate();

          if (triggered) {
            if (!waiter.isStopped()) {
              handleTriggered(context);
            }

            break;
          }

          await waiter.wait();
        } catch (err) {
          if (!waiter.isStopped()) {
            log.error(ns`monitoring`, 'Error polling teleop state', { err });

            // Monitoring error - fail the routine
            this.setVariable('exitReason', 'Error');

            const fallbackMessage = `Failed to monitor teleop state: ${(err as Error)?.message}`;

            this.events.emit('failure', {
              failure: {
                kind: FailureKind.StepPlayFailure,
                stepKind: 'Teleop',
              },
              failureReason: this.getFailureMessage(fallbackMessage),
              error: err as Error,
            });
          }

          break;
        }
      }

      waiter.stop();
    })();
  }

  /**
   * Get the failure message, preferring ControlSystemEvent and collision messages if available.
   * This step streams poses without using doMotion, so it doesn't receive
   * a MotionResult with error details. Instead, we check the robot's failure reason directly.
   * @param fallbackMessage - Message to use if no robot failure reason is available
   * @returns The failure message to display to the user
   */
  private getFailureMessage(fallbackMessage: string): string {
    const robotFailureReason = this.routineContext.robot.getFailureReason();

    if (robotFailureReason?.kind === 'controlSystemEvent') {
      return robotFailureReason.controlSystemEvent.message;
    }

    if (robotFailureReason?.kind === 'imuCollision') {
      return formatImuCollisionMessage(robotFailureReason);
    }

    if (robotFailureReason?.kind === 'collision') {
      return `Joint ${robotFailureReason.collision.joint} encountered a collision`;
    }

    return fallbackMessage;
  }

  /**
   * Extract errors from secondary bots in the teleop state.
   * Returns an array of error messages from secondary bots that are in error state.
   */
  private getSecondaryBotErrors(state: TeleopState): string[] {
    const errors: string[] = [];

    if (!state.config?.bots) {
      return errors;
    }

    for (const bot of state.config.bots) {
      if (bot.status === TeleopStateStatusEnum.Error) {
        const botName = bot.alias || bot.botId;

        const botErrors = bot.errors?.length
          ? bot.errors.map((e: string) => `${botName}: ${e}`)
          : [`${botName}: Unknown error`];

        errors.push(...botErrors);
      }
    }

    return errors;
  }
}
